import React, { useState, useCallback, useEffect, useMemo } from 'react';
import ToastContext from '../contexts/ToastContext';
import toastEmitter from '../utils/toastEmitter';
import './ToastProvider.css';

let nextId = 0;

const TYPE_META = {
  success: { icon: '✓' },
  error:   { icon: '✕' },
  warning: { icon: '⚠' },
  info:    { icon: 'ℹ' },
};

function ToastItem({ item, onClose }) {
  return (
    <div className={`era-toast era-toast-${item.type}`} role="alert" aria-live="assertive">
      <span className="era-toast-icon" aria-hidden="true">{TYPE_META[item.type]?.icon}</span>
      <span className="era-toast-message">{item.message}</span>
      <button className="era-toast-close" onClick={() => onClose(item.id)} aria-label="Dismiss notification">
        ×
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);

  const removeItem = useCallback((id) => {
    setItems(prev => prev.filter(t => t.id !== id));
  }, []);

  const addItem = useCallback((message, type = 'info', duration = 4500) => {
    const id = ++nextId;
    setItems(prev => [...prev, { id, message, type }]);
    if (duration > 0) {
      setTimeout(() => removeItem(id), duration);
    }
  }, [removeItem]);

  useEffect(() => toastEmitter.subscribe(({ message, type }) => addItem(message, type)), [addItem]);

  const toast = useMemo(() => ({
    success: (msg) => addItem(msg, 'success'),
    error:   (msg) => addItem(msg, 'error'),
    warning: (msg) => addItem(msg, 'warning'),
    info:    (msg) => addItem(msg, 'info'),
  }), [addItem]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {items.length > 0 && (
        <div className="era-toast-container">
          {items.map(item => (
            <ToastItem key={item.id} item={item} onClose={removeItem} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}
